import React, { useCallback, useEffect, useState } from "react";
import { Activity, BadgeIndianRupee, Bike, CheckCircle2, UserMinus, UserPlus } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import InactiveList from "./components/InactiveList";
import TrendBars from "./components/TrendBars";
import DriverDetailModal from "./components/DriverDetailModal";
import { money, n } from "./components/reportFormat";

export default function DriverReports({ filters }) {
  const [overview, setOverview] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [inactive, setInactive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverDetailOpen, setDriverDetailOpen] = useState(false);
  const [driverDetail, setDriverDetail] = useState(null);
  const [driverDetailLoading, setDriverDetailLoading] = useState(false);
  const [driverDetailError, setDriverDetailError] = useState("");
  const [selectedDriverRow, setSelectedDriverRow] = useState(null);
  const [driverOrdersPage, setDriverOrdersPage] = useState(1);
  const driverOrdersLimit = 10;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      ReportsService.driverOverview(filters),
      ReportsService.drivers(filters),
      ReportsService.inactiveDrivers(filters),
    ])
      .then(([a, b, c]) => {
        if (!alive) return;
        setOverview(a.data);
        setDrivers(b.data);
        setInactive(c.data);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [filters]);

  const fetchDriverDetail = useCallback(async (row, page = 1) => {
    const partnerId = row?.partner_id || row?.partnerId || row?.id;
    if (!partnerId) return;

    setSelectedDriverRow(row);
    setDriverOrdersPage(page);
    setDriverDetailOpen(true);
    setDriverDetailError("");
    setDriverDetailLoading(true);

    try {
      const res = await ReportsService.driverDetail(partnerId, {
        ordersPage: page,
        ordersLimit: driverOrdersLimit,
      });
      setDriverDetail(res?.data || res || null);
    } catch (err) {
      setDriverDetailError(err?.message || "Driver detail API failed");
    } finally {
      setDriverDetailLoading(false);
    }
  }, []);

  const openDriverDetail = useCallback((row) => {
    setDriverDetail(null);
    fetchDriverDetail(row, 1);
  }, [fetchDriverDetail]);

  const changeDriverOrdersPage = useCallback((page) => {
    if (!selectedDriverRow) return;
    fetchDriverDetail(selectedDriverRow, page);
  }, [fetchDriverDetail, selectedDriverRow]);

  const k = overview?.kpis || drivers?.stats || {};
  const rows = drivers?.rows || [];
  const bestIncome = Math.max(...rows.map((r) => Number(r.estimated_delivery_income || 0)), 0);
  const topDrivers = [...rows].sort((a, b) => Number(b.estimated_delivery_income || 0) - Number(a.estimated_delivery_income || 0)).slice(0, 7);

  return (
    <div className="space-y-5">
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={Bike} title="Total Drivers" value={n(k.total_drivers)} />
        <ReportCard icon={UserPlus} title="New Drivers" value={n(k.new_drivers)} hint="selected period" />
        <ReportCard icon={CheckCircle2} title="Active Drivers" value={n(k.active_drivers)} />
        <ReportCard icon={UserMinus} title="Inactive Drivers" value={n(k.inactive_drivers)} />
        <ReportCard icon={Activity} title="Driver App Opens" value={n(k.driver_app_open)} />
        <ReportCard icon={BadgeIndianRupee} title="Top Income" value={money(bestIncome)} />
      </div>

      {loading ? <div className="py-14 text-center font-bold text-[#d86a78]">Loading driver report...</div> : (
        <>
          <div className="grid gap-5 xl:grid-cols-[1.1fr_1fr]">
            <TrendBars rows={overview?.timeline || []} field="new_drivers" label="New drivers time line" />
            <ReportPanel title="Top earning drivers">
              <DataTable rows={topDrivers} columns={["driver_name", "assigned_tasks", "delivered_tasks", "estimated_delivery_income", "last_task_at"]} maxHeight="max-h-[260px]" onRowClick={openDriverDetail} />
            </ReportPanel>
          </div>

          <ReportPanel>
            <DataTable rows={rows} columns={["driver_name","phone","is_online","is_available","current_city","assigned_tasks","delivered_tasks","no_response_tasks","cancelled_tasks","avg_delivery_minutes","estimated_delivery_income","last_task_at"]} onRowClick={openDriverDetail} />
          </ReportPanel>

          <InactiveList title="Inactive Drivers" rows={inactive?.rows || []} />
        </>
      )}
      <DriverDetailModal
        open={driverDetailOpen}
        loading={driverDetailLoading}
        error={driverDetailError}
        detail={driverDetail}
        ordersPage={driverOrdersPage}
        ordersLimit={driverOrdersLimit}
        onOrdersPageChange={changeDriverOrdersPage}
        onClose={() => {
          setDriverDetailOpen(false);
          setDriverDetail(null);
          setDriverDetailError("");
          setSelectedDriverRow(null);
          setDriverOrdersPage(1);
        }}
      />
    </div>
  );
}
